import { message } from "antd";
import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { getSnapshot } from "@lib/authentication/SessionStore";
import Configuration from "@common/Configuration";
import { getSelectedSubTenant } from "../auth/SubTenantStore";
import { getUserManager } from "../auth/userManager";
import { translate } from "../TranslateService";
import { maintenanceMinutes, maintenanceRetryAfter } from "./maintenance";

const config: AxiosRequestConfig = {
  baseURL: String(Configuration.VITE_CONFIG_API_BASE ?? ""),
  headers: AxiosHeaders.from({
    "Content-Type": "application/json",
  }),
};

const instance: AxiosInstance = axios.create(config);

/**
 * Un solo avviso per volta: con la chiave fissa, le N richieste che cadono nella
 * stessa finestra di manutenzione aggiornano lo stesso messaggio invece di impilarne N.
 */
function notifyMaintenance(seconds: number): void {
  void message.warning({
    key: "maintenance",
    content: translate("system.maintenance.notice", { minutes: maintenanceMinutes(seconds) }),
    duration: 6,
  });
}

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const session = getSnapshot().session;
  if (!session.authenticated || !session.info?.token)
    return config;

  const headers: AxiosHeaders = AxiosHeaders.from(config.headers ?? {});
  headers.set("Authorization", `Bearer ${session.info.token}`);
  // Multi-tenant: sub-tenant (ambiente) attivo → il backend lo autorizza contro
  // il claim sub_tenants e vi risolve lo schema. Assente → il backend default 'prod'.
  const subTenant = getSelectedSubTenant();
  if (subTenant) headers.set("X-Tenant", subTenant);
  config.headers = headers;
  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      message.warning(translate("system.loginNeeded").toString(), 3);
      void getUserManager()?.signinRedirect();
    }

    // Nessun ritentativo automatico: rifare da soli una scrittura durante un
    // rilascio non è sicuro. Si avvisa e si lascia al chiamante il suo ramo d'errore.
    const retryAfter: number | null = maintenanceRetryAfter(error);
    if (retryAfter !== null) notifyMaintenance(retryAfter);

    return Promise.reject(error);
  }
);

export default instance;
